import { Box, Checkbox as CheckboxInput, Text, TextField } from "@engaging-tech/components"
import React, { useCallback, useEffect, useMemo, useState } from "react"

const Checkbox = ({ values: selectInputs, onAnswer, initialValue, allowOther }) => {
  const actualValues = selectInputs || []

  const otherOption = useMemo(() => {
    if (!allowOther) return null
    if (!actualValues || actualValues.length === 0) {
      return { id: "other", value: "Other" }
    }
    let found = actualValues.find(val => 
      val.value?.toLowerCase().includes("other") || 
      val.value?.toLowerCase() === "other" ||
      val.id?.toLowerCase().includes("other")
    )
    if (!found) {
      return { id: "other", value: "Other" }
    }
    return found
  }, [allowOther, actualValues])

  const [selected, setSelected] = useState([])
  const [otherText, setOtherText] = useState("")

  const isSelected = value => selected.includes(value)

  const updateAnswer = useCallback((selectedValues, otherValue) => {
    const normalValues = selectedValues.filter(id => !otherOption || id !== otherOption.id)
    const isOtherSelected = otherOption && selectedValues.includes(otherOption.id)

    const answer = {
      values: normalValues,
      otherValues: isOtherSelected ? [{ optionId: otherOption.id, value: otherValue || "" }] : []
    }
    onAnswer(answer)
  }, [otherOption, onAnswer])

  const handleToggle = useCallback((value) => {
    let newSelected
    let newOtherText = otherText

    if (otherOption && value === otherOption.id) {
      if (selected.includes(otherOption.id)) {
        newSelected = selected.filter(id => id !== otherOption.id)
        newOtherText = ""
      } else {
        newSelected = [...selected, otherOption.id]
      }
    } else {
      newSelected = selected.includes(value)
        ? selected.filter(id => id !== value)
        : [...selected, value]
    }

    setSelected(newSelected)
    if (otherOption && value === otherOption.id && selected.includes(otherOption.id)) {
      setOtherText("")
    }
    updateAnswer(newSelected, newOtherText)
  }, [otherOption, otherText, selected, updateAnswer])

  const handleOtherTextChange = useCallback((e) => {
    const text = e?.target?.value || e || ""
    setOtherText(text)
    updateAnswer(selected, text)
  }, [selected, updateAnswer])

  useEffect(() => {
    if (initialValue) {
      if (Array.isArray(initialValue.value)) {
        const normalValues = initialValue.value.filter(val => {
          if (typeof val === "string" && otherOption && val !== otherOption.id) {
            return !actualValues?.some(opt => opt.id === val)
          }
          return true
        })
        setSelected(normalValues)
        
        const otherTextValue = initialValue.value.find(
          val => typeof val === "string" && !actualValues?.some(opt => opt.id === val)
        )
        if (otherTextValue && otherOption) {
          setOtherText(otherTextValue)
          setSelected([...normalValues, otherOption.id])
        }
      } else if (initialValue.value?.values) {
        const baseValues = initialValue.value.values || []
        if (initialValue.value.otherValues && initialValue.value.otherValues.length > 0 && otherOption) {
          setOtherText(initialValue.value.otherValues[0].value || "")
          setSelected([...baseValues, otherOption.id])
        } else {
          setSelected(baseValues)
        }
      }
    }
  }, [initialValue, actualValues, otherOption])

  const isOtherSelected = otherOption && isSelected(otherOption.id)

  const normalValues = useMemo(() => {
    return actualValues.filter(val => !otherOption || val.id !== otherOption.id)
  }, [actualValues, otherOption])

  if (!actualValues || actualValues.length === 0) {
    return null
  }

  return (
    <Box mt={[3, 5]}>
      {normalValues.map(val => (
        <CheckboxInput
          key={val.id}
          id={val.id}
          checked={isSelected(val.id)}
          onChange={() => handleToggle(val.id)}
          label={() => <Text>{val.value}</Text>}
          colour="dark.0"
          mb={2}
        />
      ))}

      {allowOther && otherOption && (
        <>
          <CheckboxInput
            id={otherOption.id}
            checked={isOtherSelected}
            onChange={() => handleToggle(otherOption.id)}
            label={() => <Text>{otherOption.value || "Other"}</Text>}
            colour="dark.0"
            mb={2}
          />

          <Box mb={2} mt={2} ml={0} pr={3}>
            <TextField
              placeholder="Please specify..."
              value={otherText}
              onChange={handleOtherTextChange}
              disabled={!isOtherSelected}
              autoResize
              minHeight={60}
              rows={1}
            />
          </Box>
        </>
      )}
    </Box>
  )
}

export default Checkbox
